import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useTranslations } from '@/hooks/useTranslations';
import { completeTranslations } from '@/utils/completeTranslations';
import { Loader2, Database, CheckCircle, AlertCircle, Globe } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export const TranslationMigrator = () => {
  const { batchAddTranslations, loadTranslations } = useTranslations();
  const { toast } = useToast();
  const [isMigrating, setIsMigrating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [migrationStatus, setMigrationStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [migrationStats, setMigrationStats] = useState({ total: 0, processed: 0 });

  const handleMigration = async () => {
    setIsMigrating(true);
    setMigrationStatus('idle');
    setProgress(0);

    try {
      // Prepare translations for batch insert
      const translationsToInsert = [];
      
      for (const [language, translations] of Object.entries(completeTranslations)) {
        for (const [key, value] of Object.entries(translations)) {
          translationsToInsert.push({
            key,
            language,
            value,
            category: getCategoryFromKey(key)
          });
        }
      }

      setMigrationStats({ total: translationsToInsert.length, processed: 0 });

      // Process in batches of 50 for better performance
      const batchSize = 50;
      const batches = [];
      for (let i = 0; i < translationsToInsert.length; i += batchSize) {
        batches.push(translationsToInsert.slice(i, i + batchSize));
      }

      let processed = 0;
      for (const batch of batches) {
        await batchAddTranslations(batch);
        processed += batch.length;
        setProgress((processed / translationsToInsert.length) * 100);
        setMigrationStats(prev => ({ ...prev, processed }));
        
        // Small delay to prevent overwhelming the database
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      await loadTranslations(); // Reload translations from database
      setMigrationStatus('success');
      
      toast({
        title: "Migration réussie",
        description: `${translationsToInsert.length} traductions ont été migrées avec succès.`,
      });

    } catch (error) {
      console.error('Migration failed:', error);
      setMigrationStatus('error');
      
      toast({
        title: "Erreur de migration",
        description: "La migration des traductions a échoué.",
        variant: "destructive",
      });
    } finally {
      setIsMigrating(false);
    }
  };

  const getCategoryFromKey = (key: string): string => {
    if (key.startsWith('header.')) return 'header';
    if (key.startsWith('admin.')) return 'admin';
    if (key.startsWith('employee.')) return 'employee';
    if (key.startsWith('vendor.')) return 'vendor';
    if (key.startsWith('footer.')) return 'footer';
    if (key.startsWith('categories.')) return 'categories';
    if (key.startsWith('hero.')) return 'hero';
    return 'general';
  };

  // Calculate total translations
  const totalTranslations = Object.values(completeTranslations).reduce(
    (total, lang) => total + Object.keys(lang).length, 0
  );

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Migration des traductions complètes
        </CardTitle>
        <CardDescription>
          Migrer toutes les traductions (FR, EN, Lingala, Swahili) vers la base de données.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {migrationStatus === 'success' && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              {migrationStats.total} traductions ont été migrées avec succès ! 
              L'interface devrait maintenant afficher les vraies traductions.
            </AlertDescription>
          </Alert>
        )}

        {migrationStatus === 'error' && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Échec de la migration. Veuillez réessayer.
            </AlertDescription>
          </Alert>
        )}

        {isMigrating && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progression: {migrationStats.processed} / {migrationStats.total}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <div className="font-medium">Langues supportées:</div>
            <ul className="text-muted-foreground">
              <li>🇫🇷 Français</li>
              <li>🇬🇧 Anglais</li>
              <li>🇨🇩 Lingala</li>
              <li>🇹🇿 Swahili</li>
            </ul>
          </div>
          <div className="space-y-1">
            <div className="font-medium">Statistiques:</div>
            <ul className="text-muted-foreground">
              <li>Total: {totalTranslations} traductions</li>
              <li>Catégories: Admin, Header, Footer</li>
              <li>Gestion des utilisateurs</li>
              <li>Interface complète</li>
            </ul>
          </div>
        </div>

        <Button 
          onClick={handleMigration} 
          disabled={isMigrating || migrationStatus === 'success'}
          className="w-full"
        >
          {isMigrating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Migration en cours...
            </>
          ) : migrationStatus === 'success' ? (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              Migration terminée
            </>
          ) : (
            <>
              <Database className="mr-2 h-4 w-4" />
              Démarrer la migration
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground">
          Cette migration va remplacer les clés de traduction par de vraies traductions 
          dans toutes les langues supportées. L'interface sera immédiatement mise à jour.
        </p>
      </CardContent>
    </Card>
  );
};